import {
  CommunityRepository,
  PostRepository,
  UserRepository,
  createReport,
  deleteReport,
} from "@amityco/ts-sdk";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { ColumnModal, Dropdown } from "@/components";
import { DropdownOption } from "@/components/Forms/Dropdown";
import { Delete, Edit, Ellipsis, Flag, Pin, UserClose } from "@/icons";
import { useAmityAuth, useTranslate } from "@/providers/context";

import { CommunityPostModal } from "../";
import { useCommunityError } from "../../hooks/useCommunityError";
import { PostItem } from "../../types";
import { checkReportStatus, isCommunityAdmin } from "../../util";

const ConfirmPinModal = ({
  post,
  open,
  setOpen,
}: {
  post: PostItem;
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { translate } = useTranslate();
  const { handlePinError } = useCommunityError();
  const [pinLoading, setPinLoading] = useState(false);
  const isPinning = !post.pinned;

  const handlePin = useCallback(async () => {
    if (pinLoading) {
      return;
    }

    setPinLoading(true);

    try {
      await CommunityRepository.updateCommunity(post.parentFeedId, {
        metadata: { pinPostID: isPinning ? post.id : "" },
      }).catch((e) => handlePinError(e, isPinning));

      toast(
        <div className="flex items-center gap-2">
          <Pin className="h-6 w-6" />
          {translate(isPinning ? "Post pinned" : "Post unpinned")}
        </div>,
      );
    } catch (e) {
      console.error(e);
    } finally {
      setOpen(false);
      setPinLoading(false);
    }
  }, [
    pinLoading,
    isPinning,
    post,
    translate,
    setPinLoading,
    setOpen,
    handlePinError,
  ]);

  return (
    <ColumnModal
      open={open}
      onOpenChange={setOpen}
      hideTrigger
      cols={3}
      title={translate(
        isPinning
          ? "Are you sure you want to pin this post?"
          : "Are you sure you want to unpin this post?",
      )}
      primaryText={translate(isPinning ? "Pin Post" : "Unpin Post")}
      confirmIsLoading={pinLoading}
      confirmDisabled={pinLoading}
      onConfirm={handlePin}
      onCancel={() => setOpen(false)}
    >
      <div className="h-5" />
    </ColumnModal>
  );
};

const ConfirmDeleteModal = ({
  postId,
  open,
  setOpen,
  isDetailsScreen,
}: {
  postId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  isDetailsScreen?: boolean;
}) => {
  const { translate } = useTranslate();
  const { handleDeletePostError } = useCommunityError();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();

  const handleDelete = useCallback(async () => {
    if (deleteLoading) {
      return;
    }

    setDeleteLoading(true);

    try {
      await PostRepository.deletePost(postId).catch(handleDeletePostError);
      toast(
        <div className="flex items-center gap-2">
          <Delete className="h-6 w-6" />
          {`${translate("Post")} ${translate("Deleted").toLowerCase()}`}
        </div>,
      );
      if (isDetailsScreen) {
        navigate(-1);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setOpen(false);
      setDeleteLoading(false);
    }
  }, [
    deleteLoading,
    postId,
    isDetailsScreen,
    translate,
    setDeleteLoading,
    setOpen,
    navigate,
    handleDeletePostError,
  ]);

  return (
    <ColumnModal
      open={open}
      onOpenChange={setOpen}
      hideTrigger
      cols={3}
      title={translate("Are you sure you want to delete this post?")}
      primaryText={`${translate("Delete")} ${translate("Post")}`}
      danger
      confirmIsLoading={deleteLoading}
      confirmDisabled={deleteLoading}
      onConfirm={handleDelete}
      onCancel={() => setOpen(false)}
    >
      <p className="p1-regular p-6 pt-0">
        {translate("Once deleted this action cannot be undone.")}
      </p>
    </ColumnModal>
  );
};

const ConfirmBlockModal = ({
  userId,
  open,
  setOpen,
}: {
  userId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { translate } = useTranslate();
  const { handleBlockUserError } = useCommunityError();
  const [blockIsLoading, setBlockIsLoading] = useState(false);

  const handleDelete = useCallback(async () => {
    if (blockIsLoading) {
      return;
    }

    setBlockIsLoading(true);

    try {
      await UserRepository.Relationship.blockUser(userId).catch(
        handleBlockUserError,
      );
      toast(
        <div className="flex items-center gap-2">
          <Delete className="h-6 w-6" />
          {translate("User has been blocked")}
        </div>,
      );

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      console.error(e);
    } finally {
      setOpen(false);
      setBlockIsLoading(false);
    }
  }, [
    blockIsLoading,
    userId,
    translate,
    setBlockIsLoading,
    setOpen,
    handleBlockUserError,
  ]);

  return (
    <ColumnModal
      open={open}
      onOpenChange={setOpen}
      hideTrigger
      cols={3}
      title={translate("Are you sure you want to block this user?")}
      primaryText={`${translate("Yes")}, ${translate("Block user").toLowerCase()}.`}
      danger
      confirmIsLoading={blockIsLoading}
      confirmDisabled={blockIsLoading}
      onConfirm={handleDelete}
      onCancel={() => setOpen(false)}
    >
      <p className="p1-regular p-6 pt-0">
        {translate(
          "You will not be able to see any posts or comments from them in this feed.",
        )}
      </p>
    </ColumnModal>
  );
};

const ConfirmReportModal = ({
  post,
  open,
  setOpen,
  haveReported,
  setHaveReported,
}: {
  post: PostItem;
  open: boolean;
  setOpen: (open: boolean) => void;
  haveReported: boolean;
  setHaveReported: (reported: boolean) => void;
}) => {
  const { translate } = useTranslate();
  const { handleReportError } = useCommunityError();
  const [reportIsLoading, setReportIsLoading] = useState(false);

  const handleReport = useCallback(async () => {
    if (reportIsLoading) {
      return;
    }

    setReportIsLoading(true);

    try {
      if (haveReported) {
        await deleteReport("post", post.id).catch((e) =>
          handleReportError(e, true),
        );
        setHaveReported(false);
      } else {
        await createReport("post", post.id).catch(handleReportError);
        setHaveReported(true);
      }
      toast(
        translate(haveReported ? "Report removed" : "Post has been reported"),
      );
    } catch (e) {
      console.error(e);
    } finally {
      setOpen(false);
      setReportIsLoading(false);
    }
  }, [
    reportIsLoading,
    post,
    haveReported,
    setHaveReported,
    translate,
    setReportIsLoading,
    setOpen,
    handleReportError,
  ]);

  return (
    <ColumnModal
      open={open}
      onOpenChange={setOpen}
      hideTrigger
      cols={3}
      title={translate(
        haveReported
          ? "Are you sure you want to remove your report?"
          : "Are you sure you want to report this post?",
      )}
      primaryText={translate("Yes")}
      danger
      confirmIsLoading={reportIsLoading}
      confirmDisabled={reportIsLoading}
      onConfirm={handleReport}
      onCancel={() => setOpen(false)}
    >
      <div className="h-5" />
    </ColumnModal>
  );
};

export const CommunityPostDropdown = ({
  post,
  className,
  isDetailsScreen,
}: {
  post: PostItem;
  className?: string;
  isDetailsScreen?: boolean;
}) => {
  const { translate } = useTranslate();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isConfirmingPin, setIsConfirmingPin] = useState(false);
  const [isConfirmingBlock, setIsConfirmingBlock] = useState(false);
  const [isConfirmingReport, setIsConfirmingReport] = useState(false);
  const [postToEdit, setPostToEdit] = useState<PostItem>();
  const [haveReported, setHaveReported] = useState(false);
  const [isCheckingReport, setIsCheckingReport] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { authState } = useAmityAuth();
  const isAdmin = isCommunityAdmin(authState?.authenticatedUser);

  const handleDropdownOpen = useCallback(
    (open: boolean) => {
      setDropdownOpen(open);
      if (open) {
        // We can't use isReportedByMe when mapping posts due to rate limiting
        // So instead we only check when the dropdown is opened if there is a flagCount
        if (post.flagCount === 0 || isCheckingReport) {
          return;
        }
        checkReportStatus(
          "post",
          post.id,
          setIsCheckingReport,
          setHaveReported,
        );
      }
    },
    [
      setIsCheckingReport,
      setHaveReported,
      post.flagCount,
      post.id,
      isCheckingReport,
    ],
  );

  const dropdownOptions: DropdownOption[] = [];

  if (post.isOwner) {
    dropdownOptions.push({
      text: `${translate("Edit")} ${translate("Post")}`,
      icon: <Edit className="h-4 w-4" />,
      onClick: () => setPostToEdit(post),
    });
  }

  if (isAdmin && !isDetailsScreen) {
    dropdownOptions.push({
      text: translate(post.pinned ? "Unpin Post" : "Pin Post"),
      icon: <Pin className="h-4 w-4" />,
      onClick: () => setIsConfirmingPin(true),
    });
  }

  if (post.isOwner && !post.pinned) {
    dropdownOptions.push({
      text: `${translate("Delete")} ${translate("Post")}`,
      icon: <Delete className="h-4 w-4" />,
      color: "error",
      onClick: () => setIsConfirmingDelete(true),
    });
  }

  if (!post.isOwner && !isCommunityAdmin(post.postCreator)) {
    dropdownOptions.push({
      text: translate("Block user"),
      icon: <UserClose className="h-4 w-4" />,
      color: "error",
      onClick: () => setIsConfirmingBlock(true),
    });
  }

  if (!post.isOwner) {
    dropdownOptions.push({
      text: translate(haveReported ? "Remove report" : "Report post"),
      icon: <Flag className="h-4 w-4" />,
      color: haveReported ? undefined : "error",
      onClick: () => setIsConfirmingReport(true),
    });
  }

  if (dropdownOptions.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <Dropdown
        open={dropdownOpen}
        onOpenChange={handleDropdownOpen}
        className="min-w-[130px]"
        contentProps={{ align: "end" }}
        options={dropdownOptions}
        optionsClassName="capitalize"
      >
        <Ellipsis height={24} width={24} />
      </Dropdown>
      <ConfirmPinModal
        post={post}
        open={isConfirmingPin}
        setOpen={setIsConfirmingPin}
      />
      <ConfirmDeleteModal
        postId={post.id}
        open={isConfirmingDelete}
        setOpen={setIsConfirmingDelete}
        isDetailsScreen={isDetailsScreen}
      />
      <ConfirmBlockModal
        userId={post.postCreator.userId}
        open={isConfirmingBlock}
        setOpen={setIsConfirmingBlock}
      />
      <ConfirmReportModal
        post={post}
        open={isConfirmingReport}
        setOpen={setIsConfirmingReport}
        haveReported={haveReported}
        setHaveReported={setHaveReported}
      />
      <CommunityPostModal
        open={!!postToEdit}
        post={postToEdit}
        setOpen={(open) => {
          if (!open) {
            setPostToEdit(undefined);
          }
        }}
      />
    </div>
  );
};
